import { useState, Fragment, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import _noop from 'lodash/noop';

import { transformForSelect } from 'util/transformHelpers';
import { getListByScreen } from 'util/giftsLists';
import { Select, PaginationList } from 'components';
import { GIFTS_PRICES_CATALOG } from 'constants/catalogs';

import gridviewActive from 'assets/img/gridview_active.svg';
import gridviewInactive from 'assets/img/gridview_inactive.svg';
import listviewActive from 'assets/img/listview_active.svg';
import listviewInactive from 'assets/img/listview_inactive.svg';
import sortlist from 'assets/img/icon_sort.svg';

import { useWindowSize, useFirstRender } from 'hooks';

import GiftItem from './GiftItem/indexNew';
import { mapPropsToValues, VIEW_TYPES, MODES } from './config';
import styles from './styles.module.scss';

const GiftsList = ({
  title,
  data,
  onAddItem,
  onDeleteItem,
  onUpdateItem,
  onClickItem,
  defaultMode = 'pick',
  selected = [],
  showFilterByOrder = true,
  onOrderChange,
  onPaginationChange,
  onMobilePaginationChange,
  sortName = 'sort',
  setFieldValue,
  getHelpers,
  withMobileList = true,
  addGifts,
  onClickFilters = _noop,
  totalLimit
}) => {

  // console.log('title: ',title,' addGifts: ',addGifts, ' data: ',data )

  const [activeView, setActiveView] = useState(VIEW_TYPES.GRID);
  const { isMobile } = useWindowSize({ largeMobile: true })[1];

  const isFirstRender = useFirstRender();

  const hasListMode = activeView === VIEW_TYPES.LIST;
  const setList = () => setActiveView(VIEW_TYPES.LIST);
  const setGrid = () => setActiveView(VIEW_TYPES.GRID);

  const hasGuestMode = defaultMode === MODES.GUEST;
  const handleChange = useCallback(
    (page) => {
      if (onPaginationChange) onPaginationChange(page);
    },
    [onPaginationChange]
  );

  const handleMobileChange = useCallback(
    (newListBlock, page) => {
      if (onMobilePaginationChange)
        onMobilePaginationChange(newListBlock, page);
    },
    [onMobilePaginationChange]
  );

  const handleOrderChange = useCallback(
    (evt) => {
      if (onOrderChange && !isFirstRender) onOrderChange(evt);
    },
    [onOrderChange, isFirstRender]
  );

  const resetOrderField = () => {
    setFieldValue(sortName, '');
  };

  useEffect(() => {
    if (!getHelpers) return;

    const helpers = {
      resetOrder: resetOrderField,
      setFieldValue
    };

    getHelpers(helpers);
  }, [setFieldValue]);

  const productsList = useMemo(() => {
    const { list = [], mobileList = [] } = data?.results;

    if (!withMobileList) return list || [];

    return (isMobile ? mobileList : list) || [];
  }, [data, isMobile, withMobileList]);

  /* if (!hasGuestMode) {
    productsList.forEach((p) => {
      if (p.variations?.length) {
        const variantion = p.variations?.filter((v) => v.id === p.variant_id);
        p.stock = variantion?.length ? variantion[0].stock : 0;
      }
    });
  } */

  const hideSoldOutProducts = hasGuestMode
    ? productsList?.filter(
        (giftsProducts) =>
          giftsProducts.status !== false || giftsProducts.stock !== 0
      )
    : productsList;

  const totalElementsOfList = hasGuestMode
    ? hideSoldOutProducts.length
    : data.totalElements;

  const result =
    totalLimit && totalElementsOfList >= totalLimit
      ? totalLimit
      : totalElementsOfList;

  const currentList = getListByScreen(data, isMobile);
  const hasEmpty = currentList.length === 0;
  const withoutItems = hasGuestMode ? !hideSoldOutProducts.length : hasEmpty;
  const inTheLastPage = currentList.length >= data.totalElements;

  
  /* bloque de lista de productos */
  return (
    <div className={`gifts-top-list ${styles.GiftsList}`}>
      <div className={styles.header}>
        <h2 className={`${styles.title} d-none d-lg-block`}>{title}</h2>
        <div className={styles.controls}>
          <span className={`${styles.total} d-none d-lg-block`}>
            {data.totalElements} resultados
          </span>

          <div
            className={styles.showfilter}
            role="button"
            tabIndex={0}
            onClick={onClickFilters}
            onKeyPress={onClickFilters}
          >
            <img alt="Filtros" src={sortlist} /> Filtros
          </div>

          <div className={styles.viewpicker}>
            <span>Vistas</span>
            <div>
              <button onClick={setList} type="button">
                <img
                  src={
                    activeView === VIEW_TYPES.LIST
                      ? listviewActive
                      : listviewInactive
                  }
                  alt="Lista"
                />
              </button>
              <button onClick={setGrid} type="button">
                <img
                  src={
                    activeView === VIEW_TYPES.GRID
                      ? gridviewActive
                      : gridviewInactive
                  }
                  alt="mosaico"
                />
              </button>
            </div>
          </div>

          {showFilterByOrder && (
            <div className="d-none d-lg-block">
              <Select
                name={sortName}
                options={transformForSelect(
                  GIFTS_PRICES_CATALOG,
                  'value',
                  'name'
                )}
                label="Ordenar por"
                showLabel={false}
                onChange={handleOrderChange}
              >
                {({ fieldPlaceholder, onClick, value, ...selectProps }) => (
                  <div
                    className={styles.order}
                    onClick={onClick}
                    onKeyDown={onClick}
                    role="button"
                    tabIndex="0"
                  >
                    <select {...selectProps} disabled>
                      <option className="d-none" value="0">
                        {value || fieldPlaceholder}
                      </option>
                    </select>
                  </div>
                )}
              </Select>
            </div>
          )}
        </div>
      </div>
      <div
        className={`${styles.list} ${
          hasListMode ? 'row' : 'd-flex justify-content-start flex-wrap'
        }`}
      >
        {hideSoldOutProducts.map((giftProduct) => {
          const isSelected = selected.find(
            ({ id }) => Number(id) === Number(giftProduct.id)
          );

          const giftProductRender = (
            <GiftItem
              onClickGift={onClickItem}
              onAddGift={onAddItem}
              onDeleteGift={onDeleteItem}
              onUpdateGift={onUpdateItem}
              type={hasListMode ? 'list' : 'medium'}
              showBigPrice={!hasListMode}
              id={giftProduct.id}
              item={giftProduct}
              selected={isSelected}
              quantity={isSelected?.quantity || 0}
              mode={defaultMode}
              controlsClassName={styles.productControls}
              productDetailsClassName={styles.productDetails}
              bigRenderClassName={styles.bigProduct}
              addGifts={addGifts}
            />
          );
          return (
            <Fragment key={giftProduct.U || giftProduct.id}>
              {hasListMode ? (
                <div className="col-12 col-lg-6 p-0 px-2 d-flex">
                  {giftProductRender}
                </div>
              ) : (
                giftProductRender
              )}
            </Fragment>
          );
        })}
      </div>
      {!withoutItems && (isMobile ? !inTheLastPage : true) && (
        <PaginationList
          list={data}
          onChange={handleChange}
          onMobileChange={handleMobileChange}
          totalLimit={totalLimit}
        />
      )}
    </div>
  );
};

GiftsList.propTypes = {
  title: PropTypes.string,
  data: PropTypes.shape({
    results: PropTypes.shape({
      list: PropTypes.arrayOf(PropTypes.any)
    })
  }).isRequired,
  onAddItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onUpdateItem: PropTypes.func,
  onClickItem: PropTypes.func,
  defaultMode: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.object),
  showFilterByOrder: PropTypes.bool,
  onOrderChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  sortName: PropTypes.string,
  withMobileList: PropTypes.bool,
  onClickFilters: PropTypes.func
};

export default withFormik({ mapPropsToValues })(GiftsList);
